import { JsonRpcApiProviderOptions, JsonRpcProvider, Network } from 'ethers'
import { createPublicClient, custom, PublicClient } from 'viem'

import { Network as NetworkInterface } from '../../interfaces/network'
import getRootDomain from '../../utils/getRootDomain'
import { FailoverJsonRpcProvider } from './FailoverJsonRpcProvider'

const RPC_BATCH_CONFIG: Record<string, number> = {
  'drpc.org': 3, // batch of more than 3 requests are not allowed on free tier (response 500 with internal code 31)
  '1rpc.io': 3, // batch of more than 3 requests are not allowed on free tier (response 500 with internal code 31)
  'roninchain.com': 3 // batch of more than 3 results in response 400 with "too many requests"
  // Keep tatum.io config disabled - if restricted to 1 it hits their limit of 5 requests per minute anyways
  // 'tatum.io': 1 // batch calls are available for paid plans only (response 402)
}

const viemClientByProvider = new WeakMap<JsonRpcProvider, PublicClient>()

/** Some RPCs limit batching which causes immediate failures on our end, so configure the known ones */
const getBatchCountFromUrl = (rpcUrl: string): number | undefined => {
  try {
    const rootDomain = getRootDomain(rpcUrl)
    return RPC_BATCH_CONFIG[rootDomain]
  } catch {
    return undefined
  }
}

const getRpcProvider = (
  rpcUrls: NetworkInterface['rpcUrls'],
  chainId?: bigint | number,
  selectedRpcUrl?: string,
  options?: JsonRpcApiProviderOptions
) => {
  if (!rpcUrls.length) {
    throw new Error('rpcUrls must be a non-empty array')
  }

  // Order the URLs so the selected/preferred one is tried first, with the remaining
  // URLs kept as automatic failover targets (see FailoverJsonRpcProvider). Dedupe so a
  // URL that is both selected and present in the list isn't attempted twice.
  const primary = selectedRpcUrl && rpcUrls.includes(selectedRpcUrl) ? selectedRpcUrl : rpcUrls[0]

  if (!primary) {
    throw new Error('Invalid RPC URL provided')
  }

  const orderedUrls = [primary, ...rpcUrls.filter((u) => u !== primary)]

  // Batching limits are keyed by the primary URL (the one used first).
  const batchMaxCount = getBatchCountFromUrl(primary)
  const providerOptions = batchMaxCount ? { ...options, batchMaxCount } : options

  if (chainId) {
    const staticNetwork = Network.from(Number(chainId))

    if (staticNetwork) {
      return new FailoverJsonRpcProvider(orderedUrls, staticNetwork, {
        staticNetwork,
        ...providerOptions
      })
    }
  }

  return new FailoverJsonRpcProvider(orderedUrls, undefined, providerOptions)
}

const getViemClientForProvider = (provider: JsonRpcProvider): PublicClient => {
  const cached = viemClientByProvider.get(provider)
  if (cached) return cached

  const client = createPublicClient({
    transport: custom({
      request: ({ method, params }) => provider.send(method, params || [])
    })
  })

  viemClientByProvider.set(provider, client)
  return client
}

export { getRpcProvider, getViemClientForProvider }
